import React, { createContext, useContext, useEffect } from 'react';

import useMultiLanguageTranslation from 'hooks/useMultiLanguageTranslation';

import { IntelligenceFilters } from '../types';
import { insightFilterSignatureOf } from './insightFilterSignature';
import { publishScreenFilterSignature } from './screenFilterSignature';
import useInsightFilters, {
  InsightFiltersValue,
  unfilteredInsightFilters
} from './useInsightFilters';

// SP-1: the filter state reaches the cards through a provider rather than through props. The nine
// section components are fixed at `{ className? }`, so threading filters down would be a nine-file
// retrofit — and it still would not reach the breakdown overlays, which render in a portal outside
// the section tree. `useCardData` reads this context when no explicit `filters` argument is passed,
// which keeps its shipped signature backward-compatible. PLA-8815.
const InsightFiltersContext = createContext<InsightFiltersValue | null>(null);

export interface IInsightFiltersProviderProps {
  children?: React.ReactNode;
}

export const InsightFiltersProvider: React.FC<IInsightFiltersProviderProps> = ({ children }) => {
  const value = useInsightFilters();
  const { t } = useMultiLanguageTranslation();
  const signature = insightFilterSignatureOf(value, t);
  // The drawn text as well as the identity: the chips have to follow a language switch, which moves
  // the words and not the `key`. Both are primitives, so the effect below does not depend on the
  // signature object, which is rebuilt on every render.
  const { key } = signature;
  const text = signature.chips.map(chip => `${chip.label}${chip.value}`).join('|');

  // Published for the Orbit panel, which is mounted outside this provider and so cannot read the
  // context (PLA-8844 D2).
  useEffect(() => {
    publishScreenFilterSignature(signature);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [key, text]);

  // Retracted only on a real unmount, in its own effect: folding this into the cleanup above would
  // publish a null between every two filter changes, which a subscriber reads as leaving the screen.
  useEffect(() => () => publishScreenFilterSignature(null), []);

  return <InsightFiltersContext.Provider value={value}>{children}</InsightFiltersContext.Provider>;
};

export const useInsightFiltersContext = (): InsightFiltersValue =>
  useContext(InsightFiltersContext) ?? unfilteredInsightFilters();

// The seam's view of the context: `undefined` outside a provider, so an isolated card keys its read
// exactly as it did before the panel existed.
export const useActiveInsightFilters = (): IntelligenceFilters | undefined =>
  useContext(InsightFiltersContext)?.filters;

export default InsightFiltersContext;
